import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Route, Routes } from 'react-router';
import { FormatterInstanceProvider } from './core/formatter/context';
import { LanguageGuard } from './core/i18n/components/language-guard';
import { LanguageRedirect } from './core/i18n/components/language-redirect';
import { RouteParam } from './core/routing/param';
import { AccentBgProvider } from './visual-components/accent-bg-context/accent-bg-context';
import AnalyticsTracker from './modules/analytics/AnalyticsTracker';
import ErrorBoundary from './modules/error-boundary/error-boundary';
import { AddressMutation } from './modules/user-request-forms/components/address-mutation';
import { ArticleNotFoundForm } from './modules/user-request-forms/components/article-not-found-form';
import { AuthGuard } from './modules/auth/auth-guard';
import { AuthProvider } from './modules/auth/auth-provider';
import { BARCODE_SCAN_PATH } from './modules/scan-barcode/constants';
import { DeleteUser } from './modules/user-request-forms/components/delete-user';
import { FallbackUI } from './modules/error-boundary/fallback-ui';
import { InquiryAboutArticle } from './modules/user-request-forms/components/inquiry-about-article';
import { MAX_REQUEST_RETRIES } from './modules/backend/constants';
import { NewDeliveryAddress } from './modules/user-request-forms/components/new-delivery-address';
import { NewUserForExistingCustomer } from './modules/user-request-forms/components/new-user-for-existing-customer';
import { NewUserForExistingCustomerForm } from './modules/user-request-forms/components/new-user-for-existing-customer-form';
import { NewUserForNewCustomerForm } from './modules/user-request-forms/components/new-user-for-new-customer-form';
import { NotificationProvider } from './modules/notifications/components/notification-provider';
import { OrderSampleSheet } from './modules/user-request-forms/components/order-sample-sheet';
import { Overview as Article } from './pages/article/overview';
import { Overview as ArticlePreview } from './pages/article-preview/overview';
import { Overview as Cart } from './pages/cart/overview';
import { Overview as CartFinalize } from './pages/cart/finalize/overview';
import { Overview as Home } from './pages/home/overview';
import { Overview as NotFound } from './pages/not-found/overview';
import { Overview as Profile } from './pages/profile/overview';
import { Overview as ProfileSettings } from './pages/profile/settings/overview';
import { Overview as Register } from './pages/register/overview';
import { Overview as Scanner } from './pages/scan-barcode/overview';
import { ScannerBruteforceMemoryReset } from './modules/scan-barcode/components/scanner-bruteforce-memory-reset';
import { ServiceRequest } from './modules/user-request-forms/components/service-request';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: MAX_REQUEST_RETRIES,
      staleTime: 1000 * 60 * 1, // 1 minute
    },
  },
});

const App = () => {
  return (
    <AccentBgProvider>
      <ErrorBoundary fallback={({ error }) => <FallbackUI error={error} />}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <NotificationProvider>
              <AuthProvider>
                <LanguageGuard>
                  <FormatterInstanceProvider>
                    <Routes>
                      <Route element={<LanguageRedirect />} path="/" />
                      <Route path={`:${RouteParam.lang}?/`}>
                        <Route element={<Home />} path="" />
                        <Route
                          path={`article/:${RouteParam.articleId}`}
                          element={
                            <AuthGuard>
                              <Article />
                            </AuthGuard>
                          }
                        />
                        <Route element={<ArticlePreview />} path={`article-preview/:${RouteParam.articleId}`} />
                        <Route
                          path={BARCODE_SCAN_PATH}
                          element={
                            <AuthGuard>
                              <Scanner />
                            </AuthGuard>
                          }
                        />
                        <Route element={<Register />} path="register" />
                        <Route element={<NewUserForNewCustomerForm />} path="register/new-client" />
                        <Route element={<NewUserForExistingCustomerForm />} path="register/existing-client" />
                        <Route
                          path="form/inquiry-about-article"
                          element={
                            <AuthGuard>
                              <InquiryAboutArticle />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="form/article-not-found"
                          element={
                            <AuthGuard>
                              <ArticleNotFoundForm />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="cart/finalize"
                          element={
                            <AuthGuard>
                              <CartFinalize />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="cart"
                          element={
                            <AuthGuard>
                              <Cart />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="profile"
                          element={
                            <AuthGuard>
                              <Profile />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="profile/settings"
                          element={
                            <AuthGuard>
                              <ProfileSettings />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="form/address-mutation"
                          element={
                            <AuthGuard>
                              <AddressMutation />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="form/delete-user"
                          element={
                            <AuthGuard>
                              <DeleteUser />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="form/new-user"
                          element={
                            <AuthGuard>
                              <NewUserForExistingCustomer />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="form/new-delivery-address"
                          element={
                            <AuthGuard>
                              <NewDeliveryAddress />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="form/order-sample-sheet"
                          element={
                            <AuthGuard>
                              <OrderSampleSheet />
                            </AuthGuard>
                          }
                        />
                        <Route
                          path="form/service-request"
                          element={
                            <AuthGuard>
                              <ServiceRequest />
                            </AuthGuard>
                          }
                        />
                        <Route element={<NotFound />} path="*" />
                      </Route>
                    </Routes>
                    <AnalyticsTracker />
                    <ReactQueryDevtools initialIsOpen={false} />
                    <ScannerBruteforceMemoryReset />
                  </FormatterInstanceProvider>
                </LanguageGuard>
              </AuthProvider>
            </NotificationProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    </AccentBgProvider>
  );
};
export default App;
